import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaPaperPlane, FaEllipsisV, FaPaperclip, FaSmile, FaVideo, FaPhone, FaUser, FaUserFriends, FaTimes, FaUserGraduate, FaBriefcase, FaMapMarkerAlt } from 'react-icons/fa';
import { format } from 'date-fns';

// Dummy data for chat messages
const initialMessages = [
  { id: 1, sender: 'John Doe', content: "Hello! I saw that you're working at Tech Corp. I'm interested in applying there. Do you have any advice?", timestamp: new Date('2024-09-01T10:00:00'), isSent: false },
  { id: 2, sender: 'You', content: "Hi John! Absolutely, I'd be happy to help. What specific role are you interested in?", timestamp: new Date('2024-09-01T10:05:00'), isSent: true },
  { id: 3, sender: 'John Doe', content: "I'm looking at their Software Engineer positions. I have 3 years of experience in web development.", timestamp: new Date('2024-09-01T10:10:00'), isSent: false },
  { id: 4, sender: 'You', content: "That's great! Tech Corp values practical experience. I'd recommend highlighting your biggest projects and any leadership experience in your application.", timestamp: new Date('2024-09-01T10:15:00'), isSent: true },
];

// Dummy data for connections
const connections = [
  { id: 1, name: 'John Doe', avatar: 'https://i.pravatar.cc/150?img=1', lastMessage: 'Thanks for the advice!', timestamp: new Date('2024-09-01T10:20:00') },
  { id: 2, name: 'Jane Smith', avatar: 'https://i.pravatar.cc/150?img=2', lastMessage: 'Are you attending the alumni event next month?', timestamp: new Date('2024-09-01T09:45:00') },
  { id: 3, name: 'Mike Johnson', avatar: 'https://i.pravatar.cc/150?img=3', lastMessage: "I'd love to catch up sometime this week.", timestamp: new Date('2024-08-31T18:30:00') },
  { id: 4, name: 'Sarah Williams', avatar: 'https://i.pravatar.cc/150?img=4', lastMessage: 'Congratulations on your new position!', timestamp: new Date('2024-08-30T14:15:00') },
];



const ConnectionsTab = () => {
  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showProfilePanel, setShowProfilePanel] = useState(false);

  const handleSendMessage = () => {
    if (newMessage.trim() !== '') {
      const message = {
        id: messages.length + 1,
        sender: 'You',
        content: newMessage,
        timestamp: new Date(),
        isSent: true,
      };
      setMessages([...messages, message]);
      setNewMessage('');
    }
  };

  const filteredConnections = connections.filter(connection =>
    connection.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-200px)] bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Connections sidebar */}
      <div className="w-1/3 border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              placeholder="Search connections..."
              className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredConnections.map((connection) => (
            <motion.div
              key={connection.id}
              className={`flex items-center p-4 hover:bg-gray-100 cursor-pointer ${selectedConnection?.id === connection.id ? 'bg-blue-50' : ''}`}
              onClick={() => setSelectedConnection(connection)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <img src={connection.avatar} alt={connection.name} className="w-12 h-12 rounded-full mr-4" />
              <div className="flex-1">
                <h3 className="font-semibold">{connection.name}</h3>
                <p className="text-sm text-gray-500 truncate-1">{connection.lastMessage}</p>
              </div>
              <span className="text-xs text-gray-400">{format(connection.timestamp, 'MMM d')}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {selectedConnection ? (
          <>
            {/* Chat header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center">
                <img src={selectedConnection.avatar} alt={selectedConnection.name} className="w-10 h-10 rounded-full mr-3" />
                <div>
                  <h2 className="font-semibold">{selectedConnection.name}</h2>
                  <p className="text-xs text-gray-500">Software Engineer at Tech Corp</p>
                </div>
              </div>
              <div className="flex space-x-4">
                <button className="text-gray-500 hover:text-gray-700">
                  <FaPhone />
                </button>
                <button className="text-gray-500 hover:text-gray-700">
                  <FaVideo />
                </button>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowProfilePanel(!showProfilePanel)}>
                  <FaUser />
                </button>
                <button className="text-gray-500 hover:text-gray-700">
                  <FaEllipsisV />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  className={`flex ${message.isSent ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${message.isSent ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded-lg p-3`}>
                    <p className="text-sm">{message.content}</p>
                    <p className="text-xs text-right mt-1 opacity-70">
                      {format(message.timestamp, 'h:mm a')}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Message input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button className="text-gray-500 hover:text-gray-700">
                  <FaPaperclip />
                </button>
                <input
                  type="text"
                  placeholder="Type a message..."
                  className="flex-1 p-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button className="text-gray-500 hover:text-gray-700">
                  <FaSmile />
                </button>
                <button
                  className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition duration-300"
                  onClick={handleSendMessage}
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <FaUserFriends className="text-6xl mb-4 text-blue-500" />
            <h2 className="text-2xl font-semibold mb-2">Welcome to Alumni Connect</h2>
            <p className="text-center max-w-md">
              Stay in touch with your college mates, seek mentorship, and grow your professional network. 
              Select a connection to start chatting!
            </p>
          </div>
        )}
      </div>

      {/* Profile Panel */}
      <AnimatePresence>
        {showProfilePanel && selectedConnection && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className="fixed right-0 top-0 h-full w-80 bg-white shadow-lg p-6 overflow-y-auto"
          >
            <button onClick={() => setShowProfilePanel(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
              <FaTimes />
            </button>
            <img src={selectedConnection.avatar} alt={selectedConnection.name} className="w-24 h-24 rounded-full mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-center mb-2">{selectedConnection.name}</h3>
            <div className="space-y-2 mb-6">
              <p className="flex items-center text-gray-600"><FaUserGraduate className="mr-2" /> Class of 2020</p>
              <p className="flex items-center text-gray-600"><FaBriefcase className="mr-2" /> Software Engineer at Tech Corp</p>
              <p className="flex items-center text-gray-600"><FaMapMarkerAlt className="mr-2" /> San Francisco, CA</p>
            </div>
            <div className="space-y-4">
              <button className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition duration-300">Send Message</button>
              <button className="w-full border border-blue-500 text-blue-500 py-2 rounded-md hover:bg-blue-50 transition duration-300">View Full Profile</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConnectionsTab;